import apiClient from "./client";
import type { Event, Stage, Session, Sponsor, SponsorWithSessions, DashboardData, Conflict } from "@/types";

export const eventsApi = {
  list: (): Promise<Event[]> =>
    apiClient.get("/events").then(r => r.data),

  get: (id: string): Promise<Event> =>
    apiClient.get(`/events/${id}`).then(r => r.data),

  create: (data: Partial<Event>): Promise<Event> =>
    apiClient.post("/events", data).then(r => r.data),

  update: (id: string, data: Partial<Event>): Promise<Event> =>
    apiClient.patch(`/events/${id}`, data).then(r => r.data),

  archive: (id: string): Promise<void> =>
    apiClient.delete(`/events/${id}`).then(r => r.data),

  dashboard: (id: string): Promise<DashboardData> =>
    apiClient.get(`/events/${id}/dashboard`).then(r => r.data),

  conflicts: (id: string): Promise<Conflict[]> =>
    apiClient.get(`/events/${id}/conflicts`).then(r => r.data),

  // Stages
  listStages: (eventId: string): Promise<Stage[]> =>
    apiClient.get(`/events/${eventId}/stages`).then(r => r.data),

  createStage: (eventId: string, data: Partial<Stage>): Promise<Stage> =>
    apiClient.post(`/events/${eventId}/stages`, data).then(r => r.data),

  updateStage: (id: string, data: Partial<Stage>): Promise<Stage> =>
    apiClient.patch(`/stages/${id}`, data).then(r => r.data),

  deleteStage: (id: string): Promise<void> =>
    apiClient.delete(`/stages/${id}`).then(r => r.data),

  // Sponsors
  listSponsors: (eventId: string): Promise<Sponsor[]> =>
    apiClient.get(`/events/${eventId}/sponsors`).then(r => r.data),

  createSponsor: (eventId: string, data: Partial<Sponsor>): Promise<Sponsor> =>
    apiClient.post(`/events/${eventId}/sponsors`, data).then(r => r.data),

  updateSponsor: (id: string, data: Partial<Sponsor>): Promise<Sponsor> =>
    apiClient.patch(`/sponsors/${id}`, data).then(r => r.data),

  deleteSponsor: (id: string): Promise<void> =>
    apiClient.delete(`/sponsors/${id}`).then(r => r.data),

  getSponsor: (id: string): Promise<SponsorWithSessions> =>
    apiClient.get(`/sponsors/${id}`).then(r => r.data),

  uploadSponsorLogo: (id: string, file: File): Promise<Sponsor> => {
    const fd = new FormData();
    fd.append("logo", file);
    return apiClient.post(`/sponsors/${id}/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },

  deleteSponsorLogo: (id: string): Promise<Sponsor> =>
    apiClient.delete(`/sponsors/${id}/logo`).then(r => r.data),

  // Sessions
  listSessions: (eventId: string, params?: Record<string, string>): Promise<Session[]> =>
    apiClient.get(`/events/${eventId}/sessions`, { params }).then(r => r.data),

  createSession: (eventId: string, data: Partial<Session>): Promise<{ session: Session; conflicts: Conflict[] }> =>
    apiClient.post(`/events/${eventId}/sessions`, data).then(r => r.data),

  // Logo
  uploadLogo: (eventId: string, file: File): Promise<Event> => {
    const form = new FormData();
    form.append("logo", file);
    return apiClient.post(`/events/${eventId}/logo`, form, {
      headers: { "Content-Type": undefined },
    }).then(r => r.data);
  },

  deleteLogo: (eventId: string): Promise<Event> =>
    apiClient.delete(`/events/${eventId}/logo`).then(r => r.data),

  // Docx exports — trigger a file download via blob.
  downloadConducteur: (
    eventId: string,
    opts: {
      orientation?: "landscape" | "portrait";
      theme?: "monochrome" | "colored";
      day?: string;       // YYYY-MM-DD; absent = all days
      stageId?: string;   // absent = all stages
    } = {},
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (opts.orientation) params.set("orientation", opts.orientation);
    if (opts.theme)       params.set("theme", opts.theme);
    if (opts.day)         params.set("day", opts.day);
    if (opts.stageId)     params.set("stageId", opts.stageId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return downloadBlob(`/events/${eventId}/export/conducteur${qs}`, "conducteur.docx");
  },

  downloadSpeakers: (eventId: string): Promise<void> =>
    downloadBlob(`/events/${eventId}/export/speakers`, "intervenants.docx"),

  downloadBriefing: (eventId: string, sessionId: string): Promise<void> =>
    downloadBlob(`/events/${eventId}/export/briefing/${sessionId}`, "briefing.docx"),

  downloadAgendaCSV: (
    eventId: string,
    opts: { day?: string; stageId?: string } = {},
  ): Promise<void> => {
    const params = new URLSearchParams();
    if (opts.day)     params.set("day", opts.day);
    if (opts.stageId) params.set("stageId", opts.stageId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return downloadBlob(`/events/${eventId}/export/agenda.csv${qs}`, "agenda.csv");
  },

  downloadICS: (eventId: string): Promise<void> =>
    downloadBlob(`/events/${eventId}/calendar.ics`, "agenda.ics"),

  downloadSpeakerBios: (eventId: string, opts: { sessionId?: string } = {}): Promise<void> => {
    const params = new URLSearchParams();
    if (opts.sessionId) params.set("sessionId", opts.sessionId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return downloadBlob(`/events/${eventId}/export/speaker-bios${qs}`, "bios-presse.docx");
  },

  downloadSpeakerSheet: (eventId: string, speakerId: string): Promise<void> =>
    downloadBlob(`/events/${eventId}/export/speaker-sheet?speakerId=${encodeURIComponent(speakerId)}`, "confirmation.docx"),

  downloadSponsorRecap: (eventId: string, sponsorId: string): Promise<void> =>
    downloadBlob(`/events/${eventId}/export/sponsor-recap?sponsorId=${encodeURIComponent(sponsorId)}`, "recap-sponsor.docx"),

  downloadMCScript: (eventId: string, opts: { day?: string; stageId?: string } = {}): Promise<void> => {
    const params = new URLSearchParams();
    if (opts.day)     params.set("day", opts.day);
    if (opts.stageId) params.set("stageId", opts.stageId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    return downloadBlob(`/events/${eventId}/export/mc-script${qs}`, "conducteur-mc.docx");
  },
};

async function downloadBlob(url: string, fallbackFilename: string): Promise<void> {
  try {
    const res = await apiClient.get(url, { responseType: "blob" });
    triggerDownload(res.data, parseFilename(res.headers["content-disposition"]) ?? fallbackFilename);
  } catch (err: unknown) {
    const data = (err as { response?: { data?: unknown } })?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      let serverMsg: string | null = null;
      try { serverMsg = JSON.parse(text).error ?? null; } catch { /* not JSON */ }
      if (serverMsg) throw new Error(serverMsg);
    }
    throw err;
  }
}

function parseFilename(disposition: string | undefined): string | null {
  if (!disposition) return null;
  const m = disposition.match(/filename="?([^"]+)"?/);
  return m ? m[1] : null;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
